import { useState } from "react";
import { ConnectionFormDataController } from "../formController";
import { PersonConnection } from "../../types/personConnection";
import { Person } from "../../types/person";
import usePersonConnectionQuery from "../../hooks/query/connections/usePersonConnectionQuery";
import { CreateEdgeData } from "../../hooks/query/connections/connectionQueryClient";
import usePersonConnectionLogic from "../../hooks/logic/connections/personConnectionLogic";

interface PersonConnectionController {
  from?: Person | null;
  to?: Person | null;
}

const usePersonConnectionController = ({
  from = null,
  to = null,
}: PersonConnectionController): ConnectionFormDataController<
  PersonConnection,
  Person,
  Person
> => {
  if ((from === null && to === null) || (from !== null && to !== null)) {
    throw new Error("Exactly one of 'from' or 'to' must be specified");
  }

  const [id, setId] = useState<string | null>(null);
  const direction: "inbound" | "outbound" =
    from === null ? "inbound" : "outbound";
  const anchorEntity: Person = (from || to)!;
  const { useFetchById, useUpdate, useCreate } = usePersonConnectionQuery();
  const { defaultPersonConnection } = usePersonConnectionLogic(from, to);
  const fetchById = useFetchById(id || "");
  const update = useUpdate();
  const create = useCreate();
  const [isDialogShown, setShowDialog] = useState<boolean>(false);

  const openCreateDialog = () => {
    setId(null);
    setShowDialog(true);
  };

  const openEditDialog = (id: string) => {
    setId(id);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setId(null);
  };

  const fetchData = async (): Promise<PersonConnection> => {
    if (id === null) {
      return defaultPersonConnection;
    }
    const res = await fetchById.refetch();
    const connectionData = res.data;
    if (!connectionData || res.isError) {
      throw new Error("Person Connection data not found");
    }
    return connectionData;
  };

  const onSubmit = async (data: Partial<PersonConnection>) => {
    const metadata: Partial<Omit<PersonConnection, "_from" | "_to">> = {
      notes: data.notes,
      system_person_connection_type: data.system_person_connection_type,
      internal_notes: data.internal_notes,
      entity_status_of_proofreading: data.entity_status_of_proofreading,
    };
    const updateData: CreateEdgeData<PersonConnection> = {
      _from: data.fromDoc?._id || from?._id || "",
      _to: data.toDoc?._id || to?._id || "",
      metadata,
    };

    let result;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...metadata },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update person connection");
      }
    } else {
      result = await create.mutateAsync(updateData);
      if (!result || create.isError) {
        throw new Error("Failed to create person connection");
      }
    }

    setId(null);
    return result;
  };

  const isCreateMode = id === null;
  const title = () =>
    `${isCreateMode ? "Create New" : "Edit"} Person Connection`;

  return {
    defaultValues: defaultPersonConnection,
    isCreateMode,
    title,
    fetchData,
    onSubmit,
    formCallback: closeDialog,
    openEditDialog,
    openCreateDialog,
    closeDialog,
    isDialogShown,
    direction,
    anchorEntity,
    from: from || null,
    to: to || null,
  };
};

export default usePersonConnectionController;
